import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Card, Row, Col, Badge } from 'react-bootstrap';
import Flex from 'components/common/Flex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BookingInPhotoStep from './BookingInPhotoStep';

const STAGES = {
  LIST: 'LIST',
  PHOTO: 'PHOTO'
};

const BookingInIncorrectStep = ({ grnNo, recordId, app, s3Prefix, onNext, onCancel }) => {
  const [items, setItems] = useState([]);
  const [currentStage, setCurrentStage] = useState(STAGES.LIST);
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState({
    code: '',
    actualReceived: 1,
    delNoteQty: 1,
    binBarCode: '',
    description: ''
  });

  const handleAddItem = () => {
    if (!newItem.code || newItem.actualReceived < 0) return;
    setItems([...items, { ...newItem }]);
    setNewItem({ code: '', actualReceived: 1, delNoteQty: 1, binBarCode: '', description: '' });
    setIsAdding(false);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  // If in PHOTO stage, render the Photo Step
  if (currentStage === STAGES.PHOTO) {
    return (
      <BookingInPhotoStep
        key={app}
        title="Difference Photos"
        grnNo={grnNo}
        recordId={recordId}
        app={app}
        s3Prefix={s3Prefix}
        onNext={() => onNext(items)}
        isOptional={false}
        nextText="Save & Send Mail"
      />
    );
  }

  return (
    <div className="p-3">
      {/* Header Section */}
      <Flex justifyContent="between" alignItems="center" className="mb-3 flex-wrap gap-2">
        <div>
          <h4 className="mb-0">Differences (Not on WPO)</h4>
          <p className="text-muted small mb-0">Total: <Badge bg="info" pill>{items.length}</Badge> items added</p>
        </div>
        {!isAdding && (
          <Button variant="primary" className="rounded-pill px-3 shadow-sm" onClick={() => setIsAdding(true)}>
            <FontAwesomeIcon icon="plus" className="me-2" /> Add Item
          </Button>
        )}
      </Flex>

      {/* Add Item Form (Pro Max Card Layout) */}
      {isAdding && (
        <Card className="mb-4 shadow-sm border-primary" style={{ backgroundColor: 'rgba(var(--falcon-primary-rgb), 0.05)' }}>
          <Card.Body className="p-3">
            <h6 className="mb-3 text-primary fw-bold">New Incorrect Item</h6>
            <Row className="g-3">
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold">Part Code *</Form.Label>
                  <Form.Control 
                    value={newItem.code} 
                    onChange={e => setNewItem({...newItem, code: e.target.value})}
                    placeholder="Enter code..."
                    className="shadow-none border-2"
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold">Description</Form.Label>
                  <Form.Control 
                    value={newItem.description} 
                    onChange={e => setNewItem({...newItem, description: e.target.value})}
                    placeholder="Brief description..."
                    className="shadow-none"
                  />
                </Form.Group>
              </Col>
              <Col xs={6} sm={4}>
                <Form.Group>
                  <Form.Label className="small fw-bold">Actual Qty</Form.Label>
                  <Form.Control 
                    type="number"
                    value={newItem.actualReceived} 
                    onChange={e => setNewItem({...newItem, actualReceived: Number(e.target.value)})}
                    className="shadow-none"
                  />
                </Form.Group>
              </Col>
              <Col xs={6} sm={4}>
                <Form.Group>
                  <Form.Label className="small fw-bold">Del Note Qty</Form.Label>
                  <Form.Control 
                    type="number"
                    value={newItem.delNoteQty} 
                    onChange={e => setNewItem({...newItem, delNoteQty: Number(e.target.value)})}
                    className="shadow-none"
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Group>
                  <Form.Label className="small fw-bold">Bin Barcode</Form.Label>
                  <Form.Control 
                    value={newItem.binBarCode} 
                    onChange={e => setNewItem({...newItem, binBarCode: e.target.value})}
                    placeholder="Scan or enter Bin..."
                    className="shadow-none"
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button variant="outline-secondary" size="sm" className="px-3" onClick={() => setIsAdding(false)}>
                Cancel
              </Button>
              <Button variant="success" size="sm" className="px-4" onClick={handleAddItem} disabled={!newItem.code}>
                <FontAwesomeIcon icon="check" className="me-2" /> Save Item
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Item List (Responsive Cards) */}
      <div className="mb-4" style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {items.length === 0 && !isAdding ? (
          <div className="text-center py-5 bg-light rounded-3 border-dashed border-2">
            <FontAwesomeIcon icon="exclamation-circle" size="3x" className="text-300 mb-3" />
            <h5 className="text-muted">No incorrect items added yet</h5>
            <p className="small text-muted mb-0">Click "Add Item" to record differences found during delivery.</p>
          </div>
        ) : (
          items.map((item, idx) => (
            <Card key={idx} className="mb-2 border-0 shadow-sm overflow-hidden item-card">
              <div className="d-flex align-items-stretch" style={{ minHeight: '80px' }}>
                <div className="bg-primary p-2 d-flex align-items-center justify-content-center" style={{ width: '40px' }}>
                  <span className="text-white small fw-bold">{idx + 1}</span>
                </div>
                <Card.Body className="p-3 d-flex flex-column justify-content-center w-100">
                  <div className="d-flex justify-content-between align-items-start fw-bold">
                    <div className="text-primary truncate-1-line" style={{ maxWidth: '80%' }}>
                      {item.code} {item.description && <span className="text-muted fw-normal small mx-1">| {item.description}</span>}
                    </div>
                    <Button variant="link" className="text-danger p-0 ms-2" onClick={() => removeItem(idx)}>
                      <FontAwesomeIcon icon="trash-alt" />
                    </Button>
                  </div>
                  <div className="d-flex flex-wrap gap-3 mt-2 small text-700">
                    <div>Actual: <span className="fw-bold text-dark">{item.actualReceived}</span></div>
                    <div>Del Note: <span className="fw-bold text-dark">{item.delNoteQty}</span></div>
                    {item.binBarCode && (
                      <div>Bin: <span className="badge bg-soft-info text-info ml-1">{item.binBarCode}</span></div>
                    )}
                  </div>
                </Card.Body>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Navigation Footer */}
      <Flex justifyContent="between" className="mt-auto pt-3 border-top">
        <Button variant="outline-secondary" className="px-4" onClick={() => onCancel && onCancel()}>
          Skip
        </Button>
        <Button 
          variant="primary" 
          className="px-5 shadow" 
          onClick={() => setCurrentStage(STAGES.PHOTO)} 
          disabled={items.length === 0}
        >
          Proceed to Photos <FontAwesomeIcon icon="arrow-right" className="ms-2" />
        </Button>
      </Flex>
    </div>
  );
};

BookingInIncorrectStep.propTypes = {
  grnNo: PropTypes.string.isRequired,
  recordId: PropTypes.number,
  app: PropTypes.string,
  s3Prefix: PropTypes.string.isRequired,
  onNext: PropTypes.func.isRequired,
  onCancel: PropTypes.func
};

export default BookingInIncorrectStep;
